import React, { useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { isClientErrorCode } from 'util/error-codes';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import FallbackTopics from 'assets/images/fallback-topics.svg';
import GenericErrorIcon from 'assets/images/generic-error.react.svg';
import { isAdvancedCustomizationEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { HelpCenterContainer } from 'view/help-center/container';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import * as bp from 'view/styles/breakpoints';
import * as fonts from 'view/styles/fonts';
import * as grid from 'view/styles/grid';
import { Title, TopicsSection, TopicsList as StyledList } from 'view/styles/help-center';
import { AnalyticsContext, useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { HTTP } from '@atlassian/help-center-common-component/constants';
import { ReactPortal } from '@atlassian/help-center-common-component/react-portal';
import TopicsRoot from '@atlassian/help-center-common-component/topics-root';
import { getHelpCenterAri, getCloudId } from '@atlassian/help-center-common-util/meta';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import fallbackMessage from '@atlassian/list-of-topics-element/src/ui/messages';
import { ExperienceSuccess } from '@atlassian/ufo';

import { helpCenterTopicsProductExperience } from '../experiences';
import { Topic } from '../topic';
import type { topicsQuery } from './__generated__/topicsQuery.graphql';
import messages from './messages';
interface Props {
    isDefaultViewLoading: boolean;
    shouldAlignWithHelpCenterContainer: boolean;
    fetchKey: number;
    disableAllLinks?: boolean;
}

const Topics: FC<Props> = ({ isDefaultViewLoading, shouldAlignWithHelpCenterContainer, fetchKey, disableAllLinks }) => {
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();
    const { helpCenter } = useLazyLoadQuery<topicsQuery>(
        graphql`
            query topicsQuery($helpCenterAri: ID!, $cloudId: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: ["HelpCenterAggBeta"]) {
                        ... on HelpCenter {
                            topics {
                                topicId
                                properties
                                ... on HelpCenterTopic {
                                    ...topicFragment
                                }
                                items {
                                    edges {
                                        node {
                                            data {
                                                __typename
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        ... on QueryError {
                            message
                            extensions {
                                statusCode
                            }
                            __typename
                        }
                    }
                }
            }
        `,
        {
            helpCenterAri: getHelpCenterAri(),
            cloudId: getCloudId(),
        },
        {
            fetchKey,
        }
    );
    const topics = helpCenter?.helpCenterById?.topics || [];

    const visibleTopics = [
        ...topics.reduce<typeof topics>((acc, current) => {
            try {
                // Suppressing existing violation. Please fix this.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const properties = JSON.parse(current.properties);
                if (
                    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    properties?.hidden === 'true' ||
                    current.items?.edges?.every((o) => o?.node?.data?.__typename === 'HelpObjectStoreQueryError')
                ) {
                    return acc;
                }
            } catch (e) {
                reportError(e);
            }
            return acc.concat(current);
        }, []),
    ];

    useEffect(() => {
        if (visibleTopics.length > 0) {
            const event = createAnalyticsEvent({
                analyticsType: UI_EVENT_TYPE,
                action: 'viewed',
                topics_count: visibleTopics.length,
            });
            event.fire();
        }
    }, [createAnalyticsEvent, visibleTopics.length]);

    useEffect(() => {
        try {
            localStorage.setItem('topicsCount', visibleTopics.length.toString());
        } catch (e) {
            //
        }
    });

    useEffect(() => {
        if (helpCenter?.helpCenterById?.__typename === 'QueryError') {
            const extensions = helpCenter?.helpCenterById?.extensions;
            const message = helpCenter?.helpCenterById?.message || '';
            let statusCode = HTTP.SERVER;
            if (extensions && Array.isArray(extensions) && extensions.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                statusCode = extensions[0].statusCode;
            }
            if (!isClientErrorCode(statusCode)) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                helpCenterTopicsProductExperience.failure({
                    metadata: {
                        statusCode,
                        error: message,
                    },
                });
            } else {
                // Disabling existing violations, should be fixed when revisited.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                helpCenterTopicsProductExperience.abort(); // To handle scenario when QueryError has 4xx
            }
        }
    }, [helpCenter]);

    if (visibleTopics.length === 0) {
        if (helpCenter?.helpCenterById?.__typename !== 'QueryError') {
            // Disabling existing violations, should be fixed when revisited.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            helpCenterTopicsProductExperience.abort(); // Abort is needed to segregate TTI between users with and without Topics
        }
        if (isAdvancedCustomizationEnabled() && isPageInEditMode && !isPageInPreviewMode) {
            return (
                <Box xcss={nonDraggableStyle}>
                    <EmptyState
                        header=""
                        maxImageWidth={250}
                        description={formatMessage(fallbackMessage.emptyStateDescription)}
                        imageUrl={FallbackTopics}
                        width="wide"
                    />
                </Box>
            );
        }

        // Currently, we assume that no-portal-content will be added to page before topics are evaluated.
        // Needs to be fixed later if topics response start coming before portals
        return !isDefaultViewLoading && document.getElementById('no-portal-content') != null ? (
            <ReactPortal target={document.getElementById('no-portal-content') ?? undefined}>
                <GreyWarningIcon />
                <Message>
                    <FormattedMessage {...messages.allPortalsHidden} />
                </Message>
                <ContactDescription>
                    <FormattedMessage {...messages.allPortalsHiddenAction} />
                </ContactDescription>
            </ReactPortal>
        ) : null;
    }
    const Container =
        shouldAlignWithHelpCenterContainer && visibleTopics.length === 1 ? StyledHelpCenterContainer : React.Fragment;
    return (
        <Container>
            <TopicsRoot>
                <Title>{formatMessage(messages.topicsSectionLabel)}</Title>
                <TopicsSection count={visibleTopics.length}>
                    <TopicsList count={visibleTopics.length}>
                        {visibleTopics.map((topic, index) => (
                            <ListItem key={topic.topicId}>
                                <AnalyticsContext data={{ attributes: { topicPosition: index + 1 } }}>
                                    <Topic topic={topic} disableAllLinks={disableAllLinks} />
                                </AnalyticsContext>
                            </ListItem>
                        ))}
                    </TopicsList>
                </TopicsSection>
                <ExperienceSuccess experience={helpCenterTopicsProductExperience} />
            </TopicsRoot>
        </Container>
    );
};

export default withAnalyticsContext({
    component: 'topics',
})(Topics);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicsList = styled(StyledList)<{ count: number }>`
    ${bp.fromMedium.css`
    & > li {
        flex-basis: ${({ count }) => {
            if (count > 2) {
                return '33%';
            }
            if (count === 2) {
                return '50%';
            }
            return '100%';
        }};
    };`}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ListItem = styled.li`
    margin-top: 0;
    padding: ${token('space.200', '16px')};
    box-sizing: border-box;
    overflow: hidden;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StyledHelpCenterContainer = styled(HelpCenterContainer)`
    margin-bottom: ${token('space.600', '48px')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const GreyWarningIcon = styled(GenericErrorIcon)`
    /* height of icon is not multiple of grid to prevent icon from being warped */
    height: ${grid.multiple(14.375).px};
    width: ${grid.multiple(11).px};
    margin-bottom: ${token('space.200', '16px')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Message = styled.div`
    ${fonts.h600};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    margin: 0 0 ${token('space.100', '8px')} 0;
    text-align: center;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ContactDescription = styled.p`
    ${fonts.regular};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    margin: 0 0 ${token('space.100', '8px')} 0;
    text-align: center;
`;

const nonDraggableStyle = xcss({
    pointerEvents: 'none',
});
